import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { toast } from "sonner";
import {
  ArrowRight,
  ShieldAlert,
  Ban,
  Receipt,
  RotateCcw,
  MessageSquareOff,
  Scale,
  XCircle,
  ChevronDown,
  Link2,
  Check,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { CANONICAL_ORIGIN } from "@/components/SEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from "@/components/ui/accordion";

const CATEGORIES = [
  {
    id: "abonelik-iptali",
    icon: Ban,
    title: "Abonelik İptali",
    desc: "İnternet, TV, mobil hat ve dijital platform aboneliklerini fesih hakkınız.",
    to: "/dosya-baslat?issue=iptal",
    anchor: "abonelik-iptali",
  },
  {
    id: "cayma-bedeli",
    icon: XCircle,
    title: "Cayma Bedeli",
    desc: "Taahhütlü sözleşmelerde cayma bedelinin sınırları ve itiraz yolları.",
    to: "/cayma-bedeli-itiraz-dilekcesi",
    anchor: "cayma-bedeli",
  },
  {
    id: "fatura-itirazi",
    icon: Receipt,
    title: "Fatura İtirazı",
    desc: "Hatalı, haksız veya iptal sonrası gelen faturalara itiraz hakkınız.",
    to: "/dosya-baslat?issue=fatura",
    anchor: "fatura-itirazi",
  },
  {
    id: "iade-cayma",
    icon: RotateCcw,
    title: "İade ve Cayma Hakkı",
    desc: "E-ticaret alışverişlerinde 14 gün cayma hakkı ve iade süreci.",
    to: "/dosya-baslat?issue=iade",
    anchor: "iade-cayma",
  },
  {
    id: "sms-eposta",
    icon: MessageSquareOff,
    title: "SMS / E-posta İptali",
    desc: "İstenmeyen ticari elektronik ileti almayı reddetme hakkınız.",
    to: "/dosya-baslat?issue=iptal",
    anchor: "sms-eposta",
  },
  {
    id: "hakem-heyeti",
    icon: Scale,
    title: "Tüketici Hakem Heyeti",
    desc: "Anlaşmazlık durumunda hakem heyetine başvurunun kuralları.",
    to: "/tuketici-hakem-heyeti",
    anchor: "hakem-heyeti",
  },
];

const LESS_KNOWN_RIGHTS = [
  {
    title: "Aboneliği iptal etmek için şubeye gitmek zorunda değilsiniz.",
    body:
      "Uzaktan (telefon, e-posta, çağrı merkezi, çevrimiçi form) satılan bir hizmeti, satın alırken kullandığınız kanalla veya benzeri bir yazılı kanalla iptal edebilmeniz gerekir. Şube dayatması yasal değildir.",
    ctaIssue: "iptal",
  },
  {
    title: "Fesih talebinden sonra şirket sizi süresiz bekletemez.",
    body:
      "İptal talebiniz belgeye bağlandıktan sonra makul ve kısa bir süre içinde işleme alınmalıdır. Genelde uygulamada 7 iş günü referans alınır; bu süre aşıldığında itiraz ve hakem heyeti yolu açıktır.",
    ctaIssue: "iptal",
  },
  {
    title: "Cayma bedeli varsa bile sınırsız olamaz.",
    body:
      "Taahhüt süresi dolmadan çıkışta talep edilen cayma bedeli, size yapılan indirim ve kampanya avantajlarının toplamını aşamaz. Fahiş cayma bedeline itiraz edebilirsiniz.",
    ctaIssue: "fatura",
  },
  {
    title: "Hatalı faturaya itiraz edebilirsiniz.",
    body:
      "Faturayı aldıktan sonra kısa süre içinde yazılı olarak itiraz ederseniz, itiraz sonuçlanana kadar tartışmalı tutar için yasal takip yapılamaz. İtirazınızı belgeleyin ve sonucu yazılı isteyin.",
    ctaIssue: "fatura",
  },
  {
    title: "Taahhüt bitince abonelik otomatik bitmeyebilir; fesih bildirimi gerekebilir.",
    body:
      "Taahhüt süresinin sona ermesi tek başına aboneliği kapatmayabilir. Aynı koşullarla akış devam edebileceği için taahhüt bitiminde açık bir fesih bildirimi vermeniz güvenlidir.",
    ctaIssue: "iptal",
  },
  {
    title: "Onayınız olmadan belirli süreli abonelik yenilenemez.",
    body:
      "Belirli süreli bir sözleşme, süresi dolduğunda sizin açık onayınız olmadan yeni bir taahhütle otomatik yenilenemez. Yenilendiği iddia edilen dönem için itiraz hakkınız vardır.",
    ctaIssue: "fatura",
  },
  {
    title: "Önden ödenen bedel, depozito veya güvence bedeli iade edilmelidir.",
    body:
      "Hizmet sonlandığında modem depozitosu, ön ödemeli bakiye, güvence bedeli gibi tutarların makul süre içinde iadesi gerekir. İade gecikirse yazılı itiraz + hakem heyeti yolunu kullanabilirsiniz.",
    ctaIssue: "iade",
  },
  {
    title: "İnternetten alınan birçok üründe 14 gün cayma hakkı vardır.",
    body:
      "Mesafeli sözleşmeyle satın alınan pek çok üründe teslim tarihinden itibaren 14 gün içinde gerekçe belirtmeksizin cayabilirsiniz. Bazı istisnalar (kişiye özel yapım, hızla bozulan ürünler vb.) vardır.",
    ctaIssue: "iade",
  },
  {
    title: "Kasa fiyatı ile etiket fiyatı farklıysa tüketici lehine olan fiyat uygulanır.",
    body:
      "Etikette görünen fiyat ile kasada okunan fiyat arasında fark varsa, düşük olan fiyat üzerinden ödeme yapmanız gerekir. Fark yüksekse fişi ve etiketi belgeleyerek itiraz edebilirsiniz.",
    ctaIssue: "iade",
  },
  {
    title: "İstenmeyen SMS ve e-postalar için ret hakkınız vardır.",
    body:
      "Ticari elektronik ileti göndericilerine \"ret\" bildirimi yapabilirsiniz ve bu bildirim üç iş günü içinde uygulanmak zorundadır. Devam eden gönderiler için İYS ve şikayet yolu açıktır.",
    ctaIssue: "iptal",
  },
];

const FAQ = [
  {
    slug: "cayma-bedeli-yasal-mi",
    q: "Cayma bedeli yasal mı?",
    a: "Taahhütlü sözleşmelerde cayma bedeli tahakkuk ettirilebilir; ancak size sağlanan indirim ve kampanya faydalarının toplamını geçemez. Fahiş cayma bedeli talepleri itiraz edilebilir.",
    ctaIssue: "fatura",
  },
  {
    slug: "aboneligimi-istedigim-zaman-iptal-edebilir-miyim",
    q: "Aboneliğimi istediğim zaman iptal edebilir miyim?",
    a: "Evet, süresiz abonelikleri istediğiniz zaman fesh edebilirsiniz. Taahhütlü aboneliklerde erken çıkışta yalnızca yasal sınırlar içindeki cayma bedeli talep edilebilir.",
    ctaIssue: "iptal",
  },
  {
    slug: "iptal-icin-subeye-gitmek-zorunda-miyim",
    q: "İptal için şubeye gitmek zorunda mıyım?",
    a: "Hayır. Aboneliği satın alırken kullandığınız kanalla veya yazılı bir kanalla (e-posta, çağrı merkezi kaydı, form) fesh edebilmeniz gerekir. Şube dayatması meşru bir gerekçe değildir.",
    ctaIssue: "iptal",
  },
  {
    slug: "sirket-iptal-talebimi-kac-gun-icinde-isleme-almak-zorunda",
    q: "Şirket iptal talebimi kaç gün içinde işleme almak zorunda?",
    a: "Sektöre göre değişmekle birlikte, uygulamada makul ve kısa bir süre — çoğu zaman 7 iş günü — beklenir. Bu süre aşıldığında itiraz ve hakem heyeti başvurusu yapılabilir.",
    ctaIssue: "iptal",
  },
  {
    slug: "hatali-faturaya-nasil-itiraz-ederim",
    q: "Hatalı faturaya nasıl itiraz ederim?",
    a: "Faturayı gördükten sonra ivedilikle yazılı olarak (e-posta, form, iadeli taahhütlü) itiraz edin; itirazınızı belge numarası, tarih ve gerekçeyle destekleyin. Sonucu yazılı isteyin.",
    ctaIssue: "fatura",
  },
  {
    slug: "taahhudum-bitti-abonelik-otomatik-kapanir-mi",
    q: "Taahhüdüm bitti, abonelik otomatik kapanır mı?",
    a: "Taahhüt bitimi tek başına aboneliği sonlandırmayabilir. Açık bir fesih bildirimi vermeniz, olası devam faturalarını önlemek için en güvenli yoldur.",
    ctaIssue: "iptal",
  },
  {
    slug: "onayim-olmadan-abonelik-yenilenirse-ne-yapabilirim",
    q: "Onayım olmadan abonelik yenilenirse ne yapabilirim?",
    a: "Onayınız olmadan yeni bir taahhütle yapılan yenilemeyi kabul etmek zorunda değilsiniz. Yenilendiği ileri sürülen dönem için yazılı itiraz + hakem heyeti yolunu kullanabilirsiniz.",
    ctaIssue: "fatura",
  },
  {
    slug: "istenmeyen-smsleri-nasil-durdurabilirim",
    q: "İstenmeyen SMS'leri nasıl durdurabilirim?",
    a: "Göndericiye \"ret\" bildirimi yapın (SMS'in içindeki iptal linki, İYS üzerinden red, e-posta). Bildirim üç iş günü içinde uygulanmak zorundadır; sürerse şikayet yolu açıktır.",
    ctaIssue: "iptal",
  },
  {
    slug: "tuketici-hakem-heyetine-ne-zaman-basvurulur",
    q: "Tüketici hakem heyetine ne zaman başvurulur?",
    a: "Yazılı itirazınız reddedildiğinde veya yanıtsız kaldığında, ilgili parasal sınırlar içinde tüketici hakem heyetine başvurabilirsiniz. Belirli tutarın üstündeki uyuşmazlıklar için tüketici mahkemesi gerekir.",
    ctaIssue: "hakem",
  },
  {
    slug: "abonelik-iptal-bu-surecte-bana-nasil-yardimci-olur",
    q: "Abonelik İptal bu süreçte bana nasıl yardımcı olur?",
    a: "Abonelik İptal, durumunuza göre sade bir Türkçe başvuru metni, gerekli belge listesi ve tarihli takip planı hazırlar. Metni siz kontrol edip gönderirsiniz — Abonelik İptal sizin yerinize başvuru yapmaz.",
    ctaIssue: "iptal",
  },
];

const INTERNAL_LINKS = [
  { to: "/abonelik-iptali", label: "Abonelik iptali" },
  { to: "/vodafone-net-iptal", label: "Vodafone Net iptal" },
  { to: "/superonline-iptal", label: "Superonline iptal" },
  { to: "/turk-telekom-internet-iptal", label: "Türk Telekom internet iptal" },
  { to: "/digiturk-iptal", label: "Digiturk iptal" },
  { to: "/dsmart-iptal", label: "D-Smart iptal" },
  { to: "/netflix-uyelik-iptali", label: "Netflix üyelik iptali" },
  { to: "/spotify-premium-iptal", label: "Spotify Premium iptal" },
  { to: "/cayma-bedeli-itiraz-dilekcesi", label: "Cayma bedeli itiraz dilekçesi" },
  { to: "/fatura-itiraz-dilekcesi", label: "Fatura itiraz dilekçesi" },
  { to: "/iptal-sonrasi-fatura-itirazi", label: "İptal sonrası fatura itirazı" },
  { to: "/tuketici-hakem-heyeti", label: "Tüketici Hakem Heyeti" },
  { to: "/trendyol-para-iadem-yatmadi", label: "Trendyol para iadem yatmadı" },
  { to: "/ayipli-urun-iade-dilekcesi", label: "Ayıplı ürün iade dilekçesi" },
];

function CategoryCard({ item }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-testid={`rights-cat-${item.id}`}
      className="group block rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#14A89F] hover:shadow-[0_10px_30px_-15px_rgba(20,168,159,0.35)] transition-all"
    >
      <div className="w-11 h-11 rounded-xl bg-[#E9F7F5] text-[#0F3D4A] flex items-center justify-center mb-4 group-hover:bg-[#14A89F] group-hover:text-white transition-colors">
        <Icon size={20} />
      </div>
      <h3 className="text-lg font-semibold text-[#0F172A]">{item.title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
      <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#14A89F]">
        Başvuru hazırla <ArrowRight size={14} />
      </div>
    </Link>
  );
}

function MiniCta({ issueId, label = "Bu konuda başvuru hazırla" }) {
  return (
    <Link
      to={`/dosya-baslat?issue=${issueId}`}
      className="inline-flex items-center gap-1.5 mt-4 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#0F172A] hover:border-[#14A89F] hover:text-[#14A89F] transition-colors"
    >
      {label} <ArrowRight size={12} />
    </Link>
  );
}

export default function HaklariniziOgrenin() {
  const canonicalPath = "/haklarinizi-ogrenin";
  const title =
    "Haklarınızı Öğrenin | Abonelik İptali, Cayma Bedeli ve Tüketici Hakları | Abonelik İptal";
  const description =
    "Abonelik iptali, cayma bedeli, fatura itirazı, iade, SMS iptali ve tüketici hakem heyeti süreçlerinde haklarınızı sade dille öğrenin.";

  // Open the FAQ item that matches the URL hash, and re-open on hash change.
  const [openFaq, setOpenFaq] = useState("");
  const [copiedSlug, setCopiedSlug] = useState("");

  const copyFaqLink = async (slug, e) => {
    e.stopPropagation();
    e.preventDefault();
    const url = `${CANONICAL_ORIGIN}/haklarinizi-ogrenin#${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
    }
    setCopiedSlug(slug);
    toast.success("Bağlantı kopyalandı");
    setTimeout(() => setCopiedSlug((s) => (s === slug ? "" : s)), 1600);
  };

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash) return;
      if (FAQ.some((f) => f.slug === hash)) {
        setOpenFaq(hash);
        // Wait for the accordion to expand, then scroll the item into view.
        setTimeout(() => {
          document
            .getElementById(hash)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: `${CANONICAL_ORIGIN}${canonicalPath}`,
      inLanguage: "tr-TR",
      isPartOf: {
        "@type": "WebSite",
        name: "Abonelik İptal",
        url: CANONICAL_ORIGIN,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: `${CANONICAL_ORIGIN}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: "Haklarınızı Öğrenin",
          item: `${CANONICAL_ORIGIN}${canonicalPath}`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Türkiye tüketicisi için pratik haklar rehberi",
      description,
      inLanguage: "tr-TR",
      mainEntityOfPage: `${CANONICAL_ORIGIN}${canonicalPath}`,
      author: { "@type": "Organization", name: "Abonelik İptal" },
      publisher: {
        "@type": "Organization",
        name: "Abonelik İptal",
        url: CANONICAL_ORIGIN,
      },
    },
  ];

  return (
    <div>
      <SEO title={title} description={description} path={canonicalPath} jsonLd={jsonLd} />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F2EDE6] to-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-14 md:pb-20">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F] mb-3">
            Tüketici rehberi
          </div>
          <h1
            data-testid="rights-hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight leading-[1.05]"
          >
            Haklarınızı Öğrenin
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-600 leading-relaxed">
            Abonelik iptali, cayma bedeli, fatura itirazı, iade ve istenmeyen
            SMS/e-posta süreçlerinde hangi haklara sahip olduğunuzu sade dille
            öğrenin.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/dosya-baslat"
              data-testid="rights-hero-cta-primary"
              className="fs-btn-primary inline-flex items-center justify-center gap-2"
            >
              Başvuru Hazırla <ArrowRight size={16} />
            </Link>
            <a
              href="#sss"
              data-testid="rights-hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-[#0F172A] hover:border-[#14A89F] hover:text-[#14A89F] transition-colors"
            >
              Sıkça Sorulan Sorulara Git
            </a>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-20">
        <div className="max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#0F172A] tracking-tight">
            Konu başlıkları
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Sizin durumunuza en yakın kategoriyi seçin — her kart, ilgili
            adımları başlatmanıza yardım eder.
          </p>
        </div>
        <div
          data-testid="rights-categories"
          className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
        >
          {CATEGORIES.map((c) => (
            <CategoryCard key={c.id} item={c} />
          ))}
        </div>
      </section>

      {/* Least Known Rights */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pt-16 md:pt-24">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E76A5A] mb-3">
            Az bilinen haklar
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-[#0F172A] tracking-tight">
            İnsanların en çok bilmediği haklar
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Aşağıdaki maddeler, günlük hayatta en sık karşınıza çıkan ancak çoğu
            kullanıcının fark etmediği tüketici haklarıdır.
          </p>
        </div>
        <ol
          data-testid="rights-less-known"
          className="mt-8 space-y-6"
        >
          {LESS_KNOWN_RIGHTS.map((r, i) => (
            <li
              key={i}
              className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-8 h-8 rounded-full bg-[#0F3D4A] text-white text-sm font-semibold flex items-center justify-center">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-semibold text-[#0F172A]">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-slate-700 leading-relaxed">{r.body}</p>
                  <MiniCta issueId={r.ctaIssue} />
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* FAQ */}
      <section id="sss" className="max-w-4xl mx-auto px-5 md:px-8 pt-16 md:pt-24 scroll-mt-24">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F] mb-3">
            SSS
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-[#0F172A] tracking-tight">
            Sıkça sorulan sorular
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Tüketici süreçlerinde en çok merak edilen sorular ve sade
            cevapları.
          </p>
        </div>

        <Accordion
          type="single"
          collapsible
          value={openFaq}
          onValueChange={setOpenFaq}
          className="mt-8 rounded-2xl border border-slate-200 bg-white divide-y divide-slate-200 overflow-hidden"
          data-testid="rights-faq"
        >
          {FAQ.map((f, i) => (
            <AccordionItem
              key={f.slug}
              id={f.slug}
              value={f.slug}
              className="border-b-0 scroll-mt-24"
              data-testid={`rights-faq-item-${i}`}
            >
              <AccordionPrimitive.Header className="flex items-stretch">
                <AccordionPrimitive.Trigger
                  className="group flex flex-1 items-center justify-between gap-3 px-5 py-5 text-left text-base font-semibold text-[#0F172A] hover:bg-slate-50 transition-colors [&[data-state=open]>svg]:rotate-180"
                >
                  <span className="flex-1 min-w-0">{f.q}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200" />
                </AccordionPrimitive.Trigger>
                <button
                  type="button"
                  onClick={(e) => copyFaqLink(f.slug, e)}
                  aria-label="Bağlantıyı kopyala"
                  title="Bağlantıyı kopyala"
                  data-testid={`rights-faq-copy-${i}`}
                  className="shrink-0 px-3 md:px-4 border-l border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#14A89F] hover:bg-slate-50 transition-colors focus:outline-none focus:text-[#14A89F]"
                >
                  {copiedSlug === f.slug ? (
                    <Check size={16} className="text-[#14A89F]" />
                  ) : (
                    <Link2 size={16} />
                  )}
                </button>
              </AccordionPrimitive.Header>
              <AccordionContent className="px-5 pb-5 text-slate-700 leading-relaxed">
                <p>{f.a}</p>
                <MiniCta issueId={f.ctaIssue} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Internal links */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-16 md:pt-24">
        <div className="max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#0F172A] tracking-tight">
            İlgili rehberler
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Aramaya geldiğiniz konuya en yakın rehber sayfalar:
          </p>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          {INTERNAL_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm text-slate-700 hover:border-[#14A89F] hover:text-[#14A89F] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </section>

      {/* Trust note */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pt-14 pb-16">
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 flex items-start gap-3">
          <ShieldAlert size={16} className="text-[#E76A5A] mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Abonelik İptal resmi kurum değildir ve hukuki danışmanlık vermez. Buradaki
            bilgiler genel bilgilendirme amaçlıdır. Başvuru yapmadan önce metni
            ve yönlendirmeleri kontrol etmeniz önerilir.
          </p>
        </div>

        <div className="mt-10 text-sm">
          <Link
            to="/dosya-baslat"
            data-testid="rights-footer-cta"
            className="fs-btn-primary inline-flex items-center gap-2"
          >
            Başvuru Hazırla <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
